import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSecurePrivacy } from "@/contexts/SecurePrivacyContext";

/**
 * SessionWatermark — subtle diagonal watermark repeated across the
 * viewport on authenticated screens. Contains a masked email, current
 * timestamp and session reference so any captured screenshot is
 * self-identifying without leaking a full email address.
 */
function maskEmail(e) {
    if (!e || typeof e !== "string" || !e.includes("@")) return "user";
    const [name, domain] = e.split("@");
    const head = name.slice(0, Math.min(2, name.length));
    return `${head}${"•".repeat(Math.max(1, name.length - 2))}@${domain}`;
}

function fmtStamp(d) {
    try {
        return d.toLocaleString(undefined, {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return d.toISOString(); }
}

export default function SessionWatermark() {
    const { user } = useAuth();
    const { watermark_enabled, sessionRef } = useSecurePrivacy();
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        if (!watermark_enabled) return;
        const t = setInterval(() => setNow(new Date()), 60_000);
        return () => clearInterval(t);
    }, [watermark_enabled]);

    if (!user?.id || !watermark_enabled) return null;
    const text = `VAULT · ${maskEmail(user.email)} · ${fmtStamp(now)} · ${sessionRef}`;
    return (
        <div
            aria-hidden="true"
            data-testid="session-watermark"
            style={{
                position: "fixed", inset: 0, pointerEvents: "none",
                zIndex: 40, overflow: "hidden", userSelect: "none",
                mixBlendMode: "multiply",
            }}
        >
            <div style={{
                position: "absolute", inset: "-50%",
                transform: "rotate(-30deg)",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
                gap: "80px 60px",
                opacity: 0.045,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.08em",
                color: "#0B1122",
                whiteSpace: "nowrap",
            }}>
                {Array.from({ length: 80 }).map((_, i) => (
                    <span key={i}>{text}</span>
                ))}
            </div>
        </div>
    );
}
